import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Languages } from "lucide-react";
import api from "../../api";

// Platform admin → Translations → Toolkit (backend clinical/care_plan/toolkit_routes.py,
// /api/admin/toolkit/*). One translation per language for every clinic. It is
// applied when a clinician gives a patient the item, in the patient's
// communication language; an item with no translation goes out in English.
const STATUS = { needs_check: "Awaiting check - not given to patients", built_in: "Built in", approved: "Published" };

function Row({ label, english, value, onChange, multiline, lang }) {
  const dir = lang === "ur" ? "rtl" : "auto";
  return (
    <tr className="border-t border-gray-100 align-top">
      <td className="px-3 py-2 w-1/2"><p className="text-[11px] font-semibold text-gray-500">{label}</p><p className="text-sm text-gray-800 whitespace-pre-line">{english}</p></td>
      <td className="px-3 py-2 w-1/2">
        {multiline
          ? <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={Math.min(8, Math.max(2, (english || "").split("\n").length + 1))} className="w-full rounded-md border border-gray-300 p-2 text-sm" dir={dir} />
          : <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-gray-300 p-2 text-sm" dir={dir} />}
      </td>
    </tr>
  );
}

export default function ToolkitTranslationsCard() {
  const [items, setItems] = useState([]);
  const [code, setCode] = useState("");
  const [info, setInfo] = useState(null);
  const [lang, setLang] = useState("hi");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getAdminToolkit().then((r) => { setItems(r.data || []); setCode((c) => c || r.data?.[0]?.code || ""); }).catch(() => {});
  }, []);
  const loadInfo = () => code && api.getToolkitTranslations(code).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { loadInfo(); }, [code]); // eslint-disable-line

  useEffect(() => {
    if (!info) return;
    const existing = info.languages.find((l) => l.code === lang)?.body;
    setForm(existing ? JSON.parse(JSON.stringify(existing)) : { title: "", description: "", body_markdown: "", entry_label: "", steps: [], timer_labels: [], fields: {} });
  }, [info, lang]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.saveToolkitTranslation(code, lang, form);
      toast.success(`Published (version ${r.data.version}) - patients get it from now on`);
      loadInfo();
      api.getAdminToolkit().then((x) => setItems(x.data || [])).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save the translation.");
    } finally {
      setSaving(false);
    }
  };

  const src = info?.source;
  const langInfo = info?.languages.find((l) => l.code === lang);
  const top = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const list = (k, i, v) => setForm((f) => { const a = [...(f[k] || [])]; a[i] = v; return { ...f, [k]: a }; });
  const field = (key, k, v) => setForm((f) => ({ ...f, fields: { ...f.fields, [key]: { ...(f.fields?.[key] || {}), [k]: v } } }));
  const fieldOpt = (key, i, v) => setForm((f) => { const o = [...(f.fields?.[key]?.options || [])]; o[i] = v; return { ...f, fields: { ...f.fields, [key]: { ...(f.fields?.[key] || {}), options: o } } }; });

  return (
    <div className="border border-gray-200 rounded-lg p-4 md:p-5 bg-gray-50 space-y-3">
      <h3 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-800"><Languages className="w-5 h-5 text-primary-600" /> Toolkit translations</h3>
      <p className="text-xs md:text-sm text-gray-500">Worksheets and exercises the patient fills in themselves. When a clinician gives one to a patient, it goes in the patient's communication language if a translation is published here, otherwise in English. Have a native speaker check each one before saving.</p>
      <div className="flex flex-wrap gap-3">
        <label className="normal-case font-sans tracking-normal text-sm"><span className="block text-xs text-gray-600 mb-1">Item</span>
          <select value={code} onChange={(e) => setCode(e.target.value)} className="rounded-md border border-gray-300 p-2 text-sm">
            {items.map((d) => <option key={d.code} value={d.code}>{d.title}{d.languages?.length ? ` (${d.languages.join(", ")})` : d.drafts?.length ? " (draft)" : ""}</option>)}
          </select>
        </label>
        <label className="normal-case font-sans tracking-normal text-sm"><span className="block text-xs text-gray-600 mb-1">Language</span>
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="rounded-md border border-gray-300 p-2 text-sm">
            {(info?.languages || []).map((l) => <option key={l.code} value={l.code}>{l.name}{l.status ? ` - ${STATUS[l.status] || l.status}` : ""}</option>)}
          </select>
        </label>
      </div>
      {langInfo?.status === "needs_check" && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 space-y-1">
          <p className="font-semibold">Not given to patients yet.</p>
          <p>{langInfo.source_note}</p>
          <p>Ask a native speaker to read every line, correct anything below, then press Save translation to publish it.</p>
        </div>
      )}
      {src && form && (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full min-w-[640px]">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500"><tr><th className="text-left px-3 py-2">English</th><th className="text-left px-3 py-2">Translation</th></tr></thead>
              <tbody>
                <Row lang={lang} label="Title" english={src.title} value={form.title} onChange={(v) => top("title", v)} />
                <Row lang={lang} label="Description" english={src.description} value={form.description} onChange={(v) => top("description", v)} multiline />
                <Row lang={lang} label="Introduction" english={src.body_markdown} value={form.body_markdown} onChange={(v) => top("body_markdown", v)} multiline />
                {src.entry_label && <Row lang={lang} label="Word for one entry (e.g. record, night)" english={src.entry_label} value={form.entry_label} onChange={(v) => top("entry_label", v)} />}
                {src.steps.map((s, i) => <Row lang={lang} key={`s${i}`} label={`Step ${i + 1}`} english={s} value={form.steps?.[i]} onChange={(v) => list("steps", i, v)} multiline />)}
                {src.timer_labels.map((s, i) => <Row lang={lang} key={`t${i}`} label={`Timer phase ${i + 1}`} english={s} value={form.timer_labels?.[i]} onChange={(v) => list("timer_labels", i, v)} />)}
                {src.fields.map((f) => [
                  ...["label", "hint", "min_label", "max_label"].filter((k) => f[k]).map((k) => (
                    <Row lang={lang} key={`${f.key}-${k}`} label={`${f.label} - ${k.replace("_", " ")}`} english={f[k]} value={form.fields?.[f.key]?.[k]} onChange={(v) => field(f.key, k, v)} />
                  )),
                  ...(f.options || []).map((o, i) => (
                    <Row lang={lang} key={`${f.key}-o${i}`} label={`${f.label} - choice ${i + 1}`} english={o} value={form.fields?.[f.key]?.options?.[i]} onChange={(v) => fieldOpt(f.key, i, v)} />
                  )),
                ])}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">{saving ? "Saving…" : "Save translation"}</button>
        </>
      )}
    </div>
  );
}
